import React from 'react';
import clsx from 'clsx';

function GroupCardHeading({ title, summary, fill }) {
  return (
    <div className={clsx('flex flex-col gap-y-0.5', fill && 'flex-1', 'min-w-0')}>
      <span className="font-medium truncate overflow-hidden">{title}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 whitespace-normal text-sm text-gray-500 dark:text-muted-foreground">
        {summary}
      </div>
    </div>
  );
}

function GroupCardTabs({ className, tabs, activeTab, onTabChange, stopPropagation }) {
  return (
    <div role="tablist" className={className}>
      {tabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.key}
          onClick={e => {
            if (stopPropagation) e.stopPropagation();
            onTabChange(tab.key);
          }}
          className={clsx(
            'pb-1.5 text-xs font-medium transition-colors border-b-2 -mb-px',
            activeTab === tab.key
              ? 'text-gray-900 dark:text-foreground border-gray-900 dark:border-foreground'
              : 'text-gray-400 dark:text-muted-foreground border-transparent hover:text-gray-600 dark:hover:text-muted-foreground',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The event ticket group card: a name, a summary line and a row of tabs, with
 * the tab content placed by the caller outside the card.
 *
 * `variant="open"` is the lone group's always-open card. `variant="collapsible"`
 * is a row in the reorderable groups list: it takes a drag handle (`leading`),
 * a trigger around the title (`wrapTitle`) and shows its tabs only while open.
 */
export default function GroupCard({
  variant = 'open',
  title,
  summary,
  actions,
  tabs,
  activeTab,
  onTabChange,
  className,
  headerClassName,
  leading,
  wrapTitle = content => content,
  showTabs = true,
  ...props
}) {
  const heading = <GroupCardHeading title={title} summary={summary} fill={variant === 'open'} />;
  const tabsProps = { tabs, activeTab, onTabChange };

  if (variant === 'collapsible') {
    return (
      <div
        className={clsx(
          'border border-gray-200 dark:border-border rounded transition-colors',
          className,
        )}
        {...props}
      >
        <div className={clsx('sticky top-0 z-10 rounded transition-colors', headerClassName)}>
          <div className="flex items-center gap-2 px-1">
            {leading}
            {wrapTitle(heading)}
            {actions}
          </div>
        </div>

        {showTabs && (
          <div className="relative">
            <GroupCardTabs className="flex gap-3 pl-11 pr-6" stopPropagation {...tabsProps} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx('border border-gray-200 dark:border-border rounded', className)}
      {...props}
    >
      <div className={clsx('py-3 truncate w-full text-left flex items-stretch', headerClassName)}>
        <div className="w-6 shrink-0" />

        <div className="flex items-center flex-1 min-w-0">{heading}</div>

        {actions}
      </div>

      <GroupCardTabs className="flex gap-3 px-6" {...tabsProps} />
    </div>
  );
}
